import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkle, ClockCounterClockwise, CaretLeft, CheckCircle } from "@phosphor-icons/react";
import MarkdownDocument from "@/components/MarkdownDocument";
import MemoActions from "@/components/MemoActions";

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  const day = 86400_000;
  if (ms < day) return "today";
  if (ms < 2 * day) return "1d ago";
  if (ms < 7 * day) return `${Math.floor(ms / day)}d ago`;
  if (ms < 28 * day) return `${Math.floor(ms / (7 * day))}w ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/**
 * WeeklyBriefDialog — YadidAi drafts a Friday partner brief for an engagement.
 *
 * Approval-gated by design: `generate-weekly-brief` returns the draft ONLY in
 * memory (no note is written). The partner then either revises the body,
 * clicks "Approve & save as memo" to persist it via `approve-weekly-brief`,
 * or discards it by closing the dialog. Nothing YadidAi produces here ever
 * becomes a final note without an explicit human approval step.
 *
 * The dialog also lists every previously approved weekly brief for this
 * engagement (via `weekly-brief-history`) so partners can revisit the arc
 * of prior Fridays.
 */
export default function WeeklyBriefDialog({ engagementId, open, onOpenChange, onSaved }) {
  const [extra, setExtra] = useState("");
  const [draft, setDraft] = useState(null);          // { title, body, note_type, status }
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [viewingHistoryNote, setViewingHistoryNote] = useState(null);

  const loadHistory = async () => {
    if (!engagementId) return;
    try {
      const { data } = await api.get(`/engagements/${engagementId}/weekly-brief-history`);
      setHistory(data.history || []);
    } catch (e) {
      console.warn("Weekly brief history load failed", e);
      setHistory([]);
    }
  };

  useEffect(() => {
    if (open) loadHistory();
    if (!open) { setViewingHistoryNote(null); setDraft(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, engagementId]);

  const draftBrief = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/engagements/${engagementId}/generate-weekly-brief`, { extra_context: extra });
      setDraft(data.draft);
      toast.info("Draft ready — review before approving");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft failed");
    } finally { setBusy(false); }
  };

  const approve = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/engagements/${engagementId}/approve-weekly-brief`, {
        title: draft.title,
        body: draft.body,
      });
      toast.success("Approved & saved as a weekly brief note");
      setHistory((prev) => [data.note, ...prev]);
      onSaved?.(data.note);
      setDraft(null);
      setExtra("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const close = () => {
    setDraft(null);
    setExtra("");
    setViewingHistoryNote(null);
    onOpenChange(false);
  };

  const showIntro = !draft && !viewingHistoryNote;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-card max-w-3xl max-h-[90vh] overflow-auto" data-testid="weekly-brief-dialog">
        <DialogDescription className="sr-only">
          Draft a Friday partner brief for this engagement. Nothing is saved until you explicitly approve the draft.
        </DialogDescription>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkle size={16} weight="fill" className="text-primary" /> Weekly partner brief
          </DialogTitle>
        </DialogHeader>

        {showIntro && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              YadidAi reads this engagement + recent notes + open tasks + last 14 days of timesheet activity, then drafts
              a Friday partner brief in five sections. <span className="text-primary">Drafts are never saved automatically</span> — review, revise,
              and approve before it becomes a memo note.
            </p>
            <div>
              <label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Extra context (optional)</label>
              <Textarea rows={3} value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="Anything the AI should weigh — a stakeholder call, a decision that landed…" className="rounded-none bg-secondary/40 mt-1" />
            </div>
            <Button data-testid="weekly-brief-draft-btn" onClick={draftBrief} disabled={busy} className="rounded-none bg-primary text-primary-foreground w-full h-11">
              <Sparkle size={14} weight="fill" className="mr-2" /> {busy ? "Drafting brief…" : "Draft weekly brief"}
            </Button>

            {history.length > 0 && (
              <div className="pt-3 border-t border-border" data-testid="weekly-brief-history">
                <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
                  <ClockCounterClockwise size={11} weight="bold" /> Previous briefs ({history.length})
                </div>
                <ul className="space-y-1.5">
                  {history.map((n) => (
                    <li key={n.id}>
                      <button
                        onClick={() => setViewingHistoryNote(n)}
                        data-testid={`weekly-brief-history-row-${n.id}`}
                        className="w-full text-left px-3 py-2 border border-border hover:border-primary/60 hover:bg-secondary/40 transition-colors flex items-start justify-between gap-3"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{n.title || "Weekly brief"}</div>
                          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">
                            {n.author_name || "YadidAi"} · {new Date(n.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                          </div>
                        </div>
                        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary shrink-0">{relTime(n.created_at)}</div>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {viewingHistoryNote && (
          <div className="space-y-4 text-sm" data-testid="weekly-brief-history-view">
            <button
              onClick={() => setViewingHistoryNote(null)}
              data-testid="weekly-brief-history-back"
              className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary inline-flex items-center gap-1.5"
            >
              <CaretLeft size={11} weight="bold" /> Back
            </button>
            <div>
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">
                {new Date(viewingHistoryNote.created_at).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
                {viewingHistoryNote.author_name && <span> · {viewingHistoryNote.author_name}</span>}
              </div>
              <h2 className="font-display text-2xl font-bold tracking-tight">{viewingHistoryNote.title}</h2>
            </div>
            <MarkdownDocument>{viewingHistoryNote.body || ""}</MarkdownDocument>
            <MemoActions markdown={viewingHistoryNote.body || ""} title={viewingHistoryNote.title || "Weekly brief"} />
          </div>
        )}

        {draft && (
          <div className="space-y-4 text-sm" data-testid="weekly-brief-draft-view">
            <div className="flex items-center gap-2 border-l-2 border-amber-500/70 bg-amber-500/5 px-3 py-2">
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-amber-500 shrink-0">Draft · not saved</div>
              <div className="text-xs text-muted-foreground">Review the read below. Approve when it's right for the client — nothing lands as a memo until you do.</div>
            </div>
            <div>
              <label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Title</label>
              <Input
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                className="rounded-none bg-secondary/40 mt-1"
                data-testid="weekly-brief-title-input"
              />
            </div>
            <MarkdownDocument>{draft.body || ""}</MarkdownDocument>
            <div className="border-t border-border pt-3">
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Edit body (markdown)</div>
              <Textarea
                rows={8}
                value={draft.body}
                onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                className="rounded-none bg-secondary/40 font-mono text-xs"
                data-testid="weekly-brief-body-input"
              />
            </div>
            <MemoActions markdown={draft.body} title={draft.title || "Weekly brief"} />
          </div>
        )}

        <DialogFooter className="gap-2">
          {draft && (
            <>
              <Button onClick={() => setDraft(null)} variant="outline" className="rounded-none" data-testid="weekly-brief-discard">
                Discard draft
              </Button>
              <Button
                onClick={approve}
                disabled={saving || !draft.body}
                className="rounded-none bg-primary text-primary-foreground"
                data-testid="weekly-brief-approve"
              >
                <CheckCircle size={12} weight="fill" className="mr-1.5" />
                {saving ? "Saving…" : "Approve & save as memo"}
              </Button>
            </>
          )}
          {(viewingHistoryNote || (!draft && history.length > 0)) && (
            <Button onClick={close} className="rounded-none bg-primary text-primary-foreground" data-testid="weekly-brief-close">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
